import { isPlatformBrowser } from '@angular/common';
import type { Signal } from '@angular/core';
import {
	computed,
	DOCUMENT,
	inject,
	Injectable,
	PLATFORM_ID,
	signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import { debounceTime, fromEvent, map, of, shareReplay, startWith } from 'rxjs';

import {
	isDesktopWidth,
	isMobileWidth,
	isSmallMobileWidth,
	isSmallTabletWidth,
	isTabletWidth,
	isXxlWidth,
	SMALL_DESKTOP,
} from '@valk-nx/services/viewport/src/lib/determine-viewports';

@Injectable({
	providedIn: 'root',
})
export class ViewPortService {
	private readonly document = inject<Document>(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);

	private readonly window!: Window;

	screenWidth$!: Observable<number>;
	isSmallMobile$: Observable<boolean>;
	isMobile$: Observable<boolean>;
	isSmallTablet$: Observable<boolean>;
	isTablet$: Observable<boolean>;
	isDesktop$: Observable<boolean>;
	isXxlWidth$: Observable<boolean>;

	screenWidth!: Signal<number>;

	isSmallMobile = computed<boolean>(() => {
		return isSmallMobileWidth(this.screenWidth());
	});
	isMobile = computed<boolean>(() => {
		return isMobileWidth(this.screenWidth());
	});
	isSmallTablet = computed<boolean>(() => {
		return isSmallTabletWidth(this.screenWidth());
	});
	isTablet = computed<boolean>(() => {
		return isTabletWidth(this.screenWidth());
	});
	isDesktop = computed<boolean>(() => {
		return isDesktopWidth(this.screenWidth());
	});
	isXxlWidth = computed<boolean>(() => {
		return isXxlWidth(this.screenWidth());
	});

	constructor() {
		if (isPlatformBrowser(this.platformId)) {
			this.window = this.document.defaultView as Window;
			this.screenWidth$ = fromEvent(this.window, 'resize').pipe(
				map(() => this.window.innerWidth),
				debounceTime(15),
				startWith(this.window.innerWidth),
				shareReplay(1),
			);
			this.screenWidth = toSignal(this.screenWidth$, {
				initialValue: this.window.innerWidth,
			});
		} else {
			this.screenWidth$ = of(SMALL_DESKTOP);
			this.screenWidth = signal(SMALL_DESKTOP);
		}
		this.isSmallMobile$ = this.screenWidth$.pipe(map(isSmallMobileWidth));
		this.isMobile$ = this.screenWidth$.pipe(map(isMobileWidth));
		this.isSmallTablet$ = this.screenWidth$.pipe(map(isSmallTabletWidth));
		this.isTablet$ = this.screenWidth$.pipe(map(isTabletWidth));
		this.isDesktop$ = this.screenWidth$.pipe(map(isDesktopWidth));
		this.isXxlWidth$ = this.screenWidth$.pipe(map(isXxlWidth));
	}
}
